import { PageHeaderMeta } from '@/components/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import { getArticle } from '@/redux/modules/article/article.selectors';
import { ArticleTagType } from '@/redux/modules/article/article.types';
import { getBaseUrl } from '@/redux/modules/config';
import { getGuideArticleUrl, logoUrl } from '@/utils/urls';

export const ArticlePageHeaderMeta = () => {
    const article = useAppSelector(getArticle);
    const baseUrl = useAppSelector(getBaseUrl);
    const articleTitle = article?.title ?? '';
    const title = useAddNotificationCountToTitle(articleTitle);

    if (!article) {
        return null;
    }

    const { author, createdAt, description, heroImageUrl, slug, subtitle, tags, topic, wordCount, updatedAt } = article;
    const canonical = `${baseUrl}${getGuideArticleUrl(topic, slug)}`;
    const tagValues = tags?.map((tag) => tag.value).filter(Boolean) ?? [];
    const keywords = tagValues.length > 0 ? tagValues.join(', ') : undefined;

    const aboutEntities =
        tags?.flatMap((tag) => {
            if (!tag.value) {
                return [];
            }

            if (tag.type === ArticleTagType.Brand) {
                return { '@type': 'Brand', name: tag.value };
            }
            if (tag.type === ArticleTagType.Category || tag.type === ArticleTagType.Keyword) {
                return { '@type': 'Thing', name: tag.value };
            }
            return [];
        }) ?? [];

    const jsonLdPayload = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'BlogPosting',
                '@id': `${canonical}#article`,
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': canonical,
                },
                headline: articleTitle,
                alternativeHeadline: subtitle,
                articleSection: topic,
                datePublished: createdAt,
                dateModified: updatedAt,
                publisher: {
                    '@type': 'Organization',
                    name: 'LiveAuctioneers',
                    url: baseUrl,
                    logo: {
                        '@type': 'ImageObject',
                        url: logoUrl,
                        width: 600,
                        height: 600,
                    },
                },
                ...(wordCount && { wordCount }),
                ...(subtitle && { alternativeHeadline: subtitle }),
                ...(description && { description }),
                ...(heroImageUrl && {
                    image: {
                        '@type': 'ImageObject',
                        url: heroImageUrl,
                        contentUrl: heroImageUrl,
                        copyrightHolder: 'LiveAuctioneers',
                        copyrightNotice: 'LiveAuctioneers',
                        creator: {
                            '@type': 'Organization',
                            name: 'LiveAuctioneers',
                            url: baseUrl,
                        },
                    },
                }),
                ...(author && {
                    author: {
                        '@type': 'Person',
                        name: author,
                    },
                }),
                ...(aboutEntities.length > 0 && { about: aboutEntities }),
            },
        ],
    };

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={heroImageUrl}
            title={title}
        >
            <script type="application/ld+json">{JSON.stringify(jsonLdPayload)}</script>
            <meta
                content="article"
                property="og:type"
            />
            <meta
                content="LiveAuctioneers"
                property="og:site_name"
            />
            {author && (
                <meta
                    content={author}
                    name="author"
                />
            )}
            {author && (
                <meta
                    content={author}
                    property="article:author"
                />
            )}
            {createdAt && (
                <meta
                    content={createdAt}
                    property="article:published_time"
                />
            )}
            {updatedAt && (
                <meta
                    content={updatedAt}
                    property="article:modified_time"
                />
            )}
            {tagValues.length > 0 &&
                tagValues.map((tagValue) => (
                    <meta
                        content={tagValue}
                        key={tagValue}
                        property="article:tag"
                    />
                ))}
            {keywords && (
                <meta
                    content={keywords}
                    name="keywords"
                />
            )}
        </PageHeaderMeta>
    );
};
